import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { practiceAreas, leadershipTeam, globalOffices, partnerEcosystem, engagementPaths } from '@/mocks/homeData';
import { Helmet } from 'react-helmet-async';

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function Home() {
  return (
    <>
    <Helmet>
  <title>
    Azosis Technologies USA - Technology Consulting & Managed IT Services | United States
  </title>

  <meta
    name="description"
    content="Azosis Technologies USA delivers technology consulting, cloud migration, managed IT services, cybersecurity, AI, and digital transformation solutions to enterprises across the United States. Incorporated in Delaware with global delivery capability."
  />

  <meta
    name="keywords"
    content="Azosis Technologies USA, technology consulting USA, IT consulting United States, cloud migration USA, managed IT services USA, enterprise technology US, cybersecurity USA, Azure consulting USA, Microsoft solutions USA"
  />

  {/* Geo */}
  <meta name="geo.region" content="US" />
  <meta
    name="geo.placename"
    content="Wilmington, Delaware, United States"
  />
  <meta
    name="geo.position"
    content="39.7447;-75.5484"
  />
  <meta
    name="ICBM"
    content="39.7447,-75.5484"
  />

  {/* Canonical */}
  <link
    rel="canonical"
    href="https://azosis.us/"
  />

  {/* Open Graph */}
  <meta
    property="og:title"
    content="Azosis Technologies USA — Technology Consulting & Managed IT Services"
  />

  <meta
    property="og:description"
    content="Technology consulting, cloud migration, managed IT services, cybersecurity, AI, and digital transformation for enterprises across the United States."
  />

  <meta
    property="og:type"
    content="website"
  />

  <meta
    property="og:url"
    content="https://azosis.us/"
  />

  <meta
    property="og:image"
    content="https://azosis.us/assets/azosis_og_image.png"
  />

  <meta
    property="og:image:width"
    content="1200"
  />

  <meta
    property="og:image:height"
    content="630"
  />

  <meta
    property="og:image:alt"
    content="Azosis Technologies USA"
  />

  <meta
    property="og:site_name"
    content="Azosis Technologies"
  />

  <meta
    property="og:locale"
    content="en_US"
  />

  {/* Twitter / X */}
  <meta
    name="twitter:card"
    content="summary_large_image"
  />

  <meta
    name="twitter:title"
    content="Azosis Technologies USA — Technology Consulting & Managed IT Services"
  />

  <meta
    name="twitter:description"
    content="Cloud migration, managed IT services, cybersecurity, AI, and technology consulting for enterprises across the United States."
  />

  <meta
    name="twitter:image"
    content="https://azosis.us/assets/azosis_og_image.png"
  />

  {/* Robots */}
  <meta
    name="robots"
    content="index, follow"
  />
</Helmet>
    <div className="bg-azosis-dark min-h-screen">
      {/* ========================================== */}
      {/* S1 — HERO */}
      {/* ========================================== */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
  {/* Background */}
<div className="absolute inset-0">
  <img
    src="/assets/home-hero-bg.webp"
    alt="Cloud technology infrastructure"
    className="w-full h-full object-cover"
  />
</div>

  {/* Overlay */}
  <div className="absolute inset-0 bg-azosis-dark/80"></div>

  {/* Glow */}
  <div className="absolute inset-0 opacity-20 pointer-events-none">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-azosis-gold/10 rounded-full blur-[180px]"></div>
  </div>

  <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 pt-12 pb-6 md:pt-16 md:pb-8">
    <div className="max-w-4xl mx-auto text-center">
      <SectionReveal delay={100}>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.15] mb-8 md:mb-10">
          Trusted technology consulting and managed services - your US technology partner.
        </h1>
      </SectionReveal>

      <SectionReveal delay={200}>
        <p className="text-azosis-textSecondary text-base md:text-lg max-w-2xl mx-auto mb-6 leading-relaxed">
          Migrating systems. Modernising workspaces. Managing technology operations long after go-live. Azosis US brings global engineering capability to enterprises across North America - backed by 16 years of multinational delivery.
        </p>
      </SectionReveal>

      <SectionReveal delay={400}>
         <Link
  to="/contact"
  className="inline-flex items-center gap-2 px-8 py-4 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
>
          Book a discovery workshop
          <i className="ri-arrow-right-line"></i>
        </Link>
      </SectionReveal>
    </div>
  </div>
</section>

      {/* ========================================== */}
      {/* S2 — SERVICES (replaces The Program) */}
      {/* ========================================== */}
      <section className="py-8 md:py-12 bg-azosis-surface">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <SectionReveal>
            <div className="text-center mb-8 md:mb-12">
              <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">Our Services</span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                Migration &middot; Workspace &middot; Managed Services
              </h2>
              <p className="text-azosis-textSecondary text-base md:text-lg max-w-2xl mx-auto">
                Three practice areas. Global delivery capability, US-incorporated entity. Every engagement starts with your problem.
              </p>
            </div>
          </SectionReveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {practiceAreas.map((area, index) => (
              <SectionReveal key={area.id} delay={index * 150}>
                <div className="group bg-azosis-dark border border-azosis-border hover:border-azosis-gold/40 transition-all duration-500 h-full flex flex-col">
                  {/* Header */}
                  <div className="p-8 md:p-10 border-b border-azosis-border">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-azosis-gold font-bold text-lg tracking-wider">{area.id}</span>
                    </div>
                    <h3 className="font-display text-xl md:text-2xl font-bold text-white mb-2">
                      {index === 0 ? 'Migration' : index === 1 ? 'Workspace' : 'Managed Services'}
                    </h3>
                    <p className="text-azosis-gold text-sm">{area.tagline}</p>
                  </div>

                  {/* Body */}
                  <div className="p-8 md:p-10 flex-1">
                    <p className="text-azosis-textSecondary text-sm leading-relaxed">
                      {index === 0
                        ? 'Cloud and system migration - from email to infrastructure. Every cutover on schedule.'
                        : index === 1
                          ? 'Secure distributed workforces, intelligent automation, and AI-assisted data operations.'
                          : 'Ongoing managed technology operations - support, optimisation, and adoption, long after go-live.'}
                    </p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>

          {/* CTA Button below cards */}
          <SectionReveal delay={400}>
            <div className="text-center mt-12">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-6 py-3 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
              >
                See All Services
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ========================================== */}
      {/* S3 — WHO WE ARE (NEW) */}
      {/* ========================================== */}
      <section className="py-8 md:py-12 bg-azosis-dark">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <SectionReveal>
            <div className="text-center mb-8 md:mb-12">
              <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">Who We Are</span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                Technology leadership built for your business.
              </h2>
              <p className="text-azosis-textSecondary text-base md:text-lg max-w-2xl mx-auto">
                Since 2010, Azosis has grown into a trusted technology partner across four geographies. Our US entity - <strong className ="text-white font-semibold">Azosis Technologies LLC </strong>, Delaware - is the Technology Hub, bringing multinational engineering capability to North American enterprises.
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Partner Ecosystem - directly below leadership */}
      <section className="py-8 md:py-12 bg-azosis-surface border-y border-azosis-border">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <SectionReveal>
            <div className="text-center mb-8 md:mb-12">
              <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase">Partner Ecosystem</span>
            </div>
          </SectionReveal>
          <SectionReveal delay={100}>
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
              {partnerEcosystem.map((partner, index) => (
                <span key={partner} className="flex items-center gap-x-2">
                  <span className="text-azosis-textMuted text-sm md:text-base font-medium tracking-wide">
                    {partner}
                  </span>
                  {index < partnerEcosystem.length - 1 && (
                    <span className="text-azosis-textMuted text-sm md:text-base">·</span>
                  )}
                </span>
              ))}
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ========================================== */}
      {/* S4 — OUR APPROACH (NEW) */}
      {/* ========================================== */}
      <section className="py-8 md:py-12 bg-azosis-dark">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <SectionReveal>
            <div className="text-center mb-16 md:mb-20">
              <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">Our Approach</span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                From first conversation to live in production.
              </h2>
            </div>
          </SectionReveal>

          {/* 3 Horizontal Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 max-w-5xl mx-auto">
            {engagementPaths.map((step, index) => (
              <SectionReveal key={step.step} delay={index * 150}>
                <div className="text-center md:text-left">
                  {/* Number Badge */}
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border-2 border-azosis-gold/50 text-azosis-gold font-display font-bold text-lg mb-5">
                    {step.step}
                  </span>

                  {/* Title */}
                  <h3 className="text-white font-semibold text-base md:text-lg mb-3">
                    {step.title}
                  </h3>

                  {/* One-line description */}
                  <p className="text-azosis-textMuted text-sm leading-relaxed">
                    {index === 0
                      ? 'Understanding your business goals and technical challenges.'
                      : index === 1
                        ? 'Validating ideas through practical implementation.'
                        : 'Long-term delivery, optimisation, and operational success.'}
                  </p>
                </div>
              </SectionReveal>
            ))}
          </div>
          {/* Business Hours Note */}
          <p className="text-center text-azosis-textMuted text-sm mt-10">
            Business hours: 10:00 AM to 4:00 PM EST,
            Mon–Fri, excluding public holidays.
          </p>

          {/* CTA #2 — FINAL CTA */}
          <SectionReveal delay={450}>
            <div className="text-center mt-16 md:mt-16">
             <Link
  to="/contact"
  className="inline-flex items-center gap-2 px-8 py-4 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
>
  Book a discovery workshop
  <i className="ri-arrow-right-line"></i>
</Link>
            </div>
          </SectionReveal>
        </div>
      </section>

    </div>
    </>
  );
}